import { Route } from '@angular/router';
import { initialDataResolver } from 'app/app.resolvers';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import {AppointmentComponent} from "./modules/user/appointment/appointment.component";
import {canActivateAuthRole} from "./core/auth/guards/keycloak.guard";
import {JoinQueueComponent} from "./modules/queue/join-queue/join-queue.component";
import {JoinQueueTabletComponent} from "./modules/queue/join-queue-tablet/join-queue-tablet.component";
import {QueueDisplayComponent} from "./modules/queue/queue-display/queue-display.component";
import {environment} from "../environments/environment";

// @formatter:off
/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
export const appRoutes: Route[] = [

    // Redirect empty path to '/example'
    { path: '', pathMatch: 'full', redirectTo: 'patients' },

    // Redirect signed-in user to the '/example'
    //
    // After the user signs in, the sign-in page will redirect the user to the 'signed-in-redirect'
    // path. Below is another redirection for that path to redirect the user to the desired
    // location. This is a small convenience to keep all main routes together here on this file.
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'patients' },

    // Auth routes for guests
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'confirmation-required', loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.routes') },
            { path: 'forgot-password', loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.routes') },
            { path: 'reset-password', loadChildren: () => import('app/modules/auth/reset-password/reset-password.routes') },
            { path: 'sign-in', loadChildren: () => import('app/modules/auth/sign-in/sign-in.routes') },
            { path: 'sign-up', loadChildren: () => import('app/modules/auth/sign-up/sign-up.routes') }
        ]
    },

    // Auth routes for authenticated users
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'sign-out', loadChildren: () => import('app/modules/auth/sign-out/sign-out.routes') },
            { path: 'unlock-session', loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.routes') }
        ]
    },

    // Landing routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'home', loadChildren: () => import('app/modules/landing/home/home.routes') },
        ]
    },

    // Public queue routes
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            { path: 'queue/join', component: JoinQueueComponent },
            { path: 'queue/register', component: JoinQueueTabletComponent },
            { path: 'queue/display', component: QueueDisplayComponent }
        ]
    },

    // Admin routes
    {
        path: '',
        component: LayoutComponent,
        canActivate: [canActivateAuthRole],
        resolve: {
            initialData: initialDataResolver
        },
        children: [
            { path: 'patients', loadChildren: () => import('app/modules/admin/patients/patients.routes').then(m => m.patientsRoutes) },
            { path: 'patient-dashboard', loadComponent: () => import('app/modules/admin/patient-care-dashboard/patient-care-dashboard.component').then(m => m.PatientCareDashboardComponent) },
            { path: 'medical-service-lines', loadComponent: () => import('app/modules/admin/medical-service-lines/medical-service-lines.component').then(m => m.MedicalServiceLinesComponent) },
            { path: 'patients/create', loadChildren: () => import('app/modules/admin/patients/patients.routes').then(m => m.patientsRoutes) },
            { path: 'patients/details', loadChildren: () => import('app/modules/admin/patients/patients.routes').then(m => m.patientsRoutes) },
            { path: 'users', loadChildren: () => import('app/modules/admin/users/users.routes').then(m => m.usersRoutes) },
            { path: 'settings', loadChildren: () => import('./modules/admin/settings/settings.routes').then(m => m.settingsRoutes) },
            { path: 'admissions', loadChildren: () => import('app/modules/admin/admissions/admissions.routes').then(m => m.admissionsRoutes) },
            {
                path: 'doctor-admissions',
                canActivate: [canActivateAuthRole],
                data: { roles: ['MEDECIN'] },
                loadComponent: () => import('app/modules/admin/admissions/doctor-list/doctor-admissions.component')
                    .then(m => m.DoctorAdmissionsComponent)
            },
            {
                path: 'nurse-admissions',
                canActivate: [canActivateAuthRole],
                data: { roles: ['INFIRMIER', 'ADMIN', 'GESTIONNAIRE'] },
                loadComponent: () => import('app/modules/admin/admissions/nurse-list/nurse-admissions.component')
                    .then(m => m.NurseAdmissionsComponent)
            },
            { path: 'acts', loadChildren: () => import('app/modules/admin/actes/actes.routes').then(m => m.actesRoutes) },
            { path: 'entries', loadChildren: () => import('app/modules/admin/entries/entries.routes').then(m => m.entriesRoutes) },
            {
                path: 'constants',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.constantsAccess },
                loadChildren: () => import('app/modules/admin/constants/constants.routes').then(m => m.constantsRoutes)
            },
            {
                path: 'consultations',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.consultationAccess },
                loadChildren: () => import('app/modules/admin/consultations/consultations.routes').then(m => m.consultationsRoutes)
            },
            { path: 'billing', loadChildren: () => import('app/modules/admin/billing/billing.routes').then(m => m.billingRoutes) },
            { path: 'mutuals', loadChildren: () => import('app/modules/admin/mutuals/mutuals.routes').then(m => m.mutualsRoutes) },
            { path: 'planning', loadChildren: () => import('app/modules/admin/schedule/schedule.routes').then(m => m.scheduleRoutes) },
            { path: 'mutualsInvoice', loadChildren: () => import('app/modules/admin/mutualsInvoice/mutualsInvoice.routes').then(m => m.mutualsInvoiceRoutes) },
            { path: 'products', loadChildren: () => import('app/modules/admin/products/products.routes').then(m => m.productsRoutes) },
            { path: 'product-kits', loadChildren: () => import('app/modules/admin/product-kits/product-kits.routes').then(m => m.productKitsRoutes) },
            {
                path: 'analysisRequest',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.analysisTabAccess },
                loadChildren: () => import('app/modules/admin/analysisRequest/analysis-request.routes').then(m => m.analysisRequestRoutes)
            },
            {
                path: 'hospitalizationNotice',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.hospitalizationAccess },
                loadChildren: () => import('app/modules/admin/hospitalization-cases/hospitalization-notice/hospitalization-notice.routes').then(m => m.hospitalizationNoticeRoutes)
            },
            {
                path: 'treatmentRecord',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.treatmentAccess },
                loadChildren: () => import('app/modules/admin/treatment-record/treatment-record.routes').then(m => m.treatmentRecordRoutes)
            },
            {
                path: 'hospitalizationFollow',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.hospitalizationAccess },
                loadChildren: () => import('app/modules/admin/hospitalization-cases/surveillance-sheet/hospitalization-follow.routes').then(m => m.hospitalizationFollowRoutes)
            },
            {
                path: 'radiologyReport',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.radioAccess },
                loadChildren: () => import('app/modules/admin/radiology-report/radiology-report.routes')
            },
            {
                path: 'hospitalization',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.hospitalizationAccess },
                loadChildren: () => import('app/modules/admin/hospitalization-cases/hospitalization.routes').then(m => m.hospitalizationRoutes)
            },
            {
                path: 'analysisResults',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.analysisResultsAccess },
                loadChildren: () => import('./modules/admin/analysis-results/analysis-results.routes').then(m => m.analysisResultsRoutes)
            },
            { path: 'queue', loadChildren: () => import('./modules/queue/queue.routes').then(m => m.queueRoutes) },
            {
                path: 'pharmacie',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.pharmacyStockAccess },
                loadChildren: () => import('./modules/admin/pharmacie/pharmacie.routes').then(m => m.pharmacieRoutes)
            },
            {
                path: 'laboratoire',
                canActivate: [canActivateAuthRole],
                data: { roles: environment.laboratoryStockAccess },
                loadChildren: () => import('./modules/admin/laboratoire/laboratoire.routes').then(m => m.laboratoireRoutes)
            }
        ]
    },

    {
        path: 'stock',
        component: LayoutComponent,
        canActivate: [canActivateAuthRole],
        resolve: {
            initialData: initialDataResolver
        },
        data: {
            roles: environment.grandStockAccess
        },
        children: [
            { path: '', loadChildren: () => import('./modules/admin/grandStock/grand-stock.routes').then(m => m.GrandStockRoutes) },
        ]
    },

    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty' // Ceci charge le cadre Fuse sans barres de navigation
        },
        children: [
            {
                path: 'appointment',
                component: AppointmentComponent
            }
        ]
    },

    //error routes
    {
        path: 'error', children: [
            { path: '403', loadChildren: () => import('app/modules/admin/pages/errors/error-403/error-403.module').then(m => m.Error403Module) }
        ]
    }
]
